import * as Sentry from '@sentry/react';

/**
 * Centralized error logging utility
 */
class ErrorLogger {
    /**
     * Log an error to Sentry
     * @param {Error|string} error - The error object or error message
     * @param {Object} context - Additional context information
     * @param {string} context.component - Component name where error occurred
     * @param {string} context.action - Action being performed when error occurred
     * @param {Object} context.extra - Additional data to include
     * @param {Object} context.tags - Custom tags for categorization
     */
    static logError(error, context = {}) {
        const { component, action, extra = {}, tags = {} } = context;
        
        Sentry.captureException(error, (scope) => {
            scope.setTags({
                component: component || 'unknown',
                action: action || 'unknown',
                ...tags
            });
            scope.setExtras({
                ...extra,
                url: window.location.href,
                userAgent: navigator.userAgent,
                timestamp: new Date().toISOString()
            });
            return scope;
        });
    }

    /**
     * Log a message to Sentry
     * @param {string} message - The message to log
     * @param {string} level - Log level (info, warning, error)
     * @param {Object} context - Additional context information
     */
    static logMessage(message, level = 'info', context = {}) {
        const { component, action, extra = {}, tags = {} } = context;
        
        Sentry.captureMessage(message, (scope) => {
            scope.setLevel(level);
            scope.setTags({
                component: component || 'unknown',
                action: action || 'unknown',
                ...tags
            });
            scope.setExtras({
                ...extra,
                url: window.location.href,
                timestamp: new Date().toISOString()
            });
            return scope;
        });
    }

    /**
     * Set user context for Sentry
     * @param {Object} user - User information
     * @param {string} user.id - User ID
     * @param {string} user.locale - User locale
     * @param {string} user.harbor - Harbor ID
     */
    static setUser(user) {
        Sentry.setUser(user);
    }

    /**
     * Set additional context for Sentry
     * @param {Object} context - Context information
     */
    static setContext(context) {
        Sentry.setContext('app', context);
    }

    /**
     * Add breadcrumb for debugging
     * @param {Object} breadcrumb - Breadcrumb information
     * @param {string} breadcrumb.message - Breadcrumb message
     * @param {string} breadcrumb.category - Breadcrumb category
     * @param {string} breadcrumb.level - Breadcrumb level
     */
    static addBreadcrumb(breadcrumb) {
        Sentry.addBreadcrumb({
            timestamp: Date.now() / 1000,
            ...breadcrumb
        });
    }

    /**
     * Log API errors specifically
     * @param {Error} error - The API error
     * @param {Object} context - Additional context
     * @param {string} context.endpoint - API endpoint
     * @param {string} context.method - HTTP method
     * @param {Object} context.requestData - Request data
     * @param {Object} context.responseData - Response data
     */
    static logApiError(error, context = {}) {
        const { endpoint, method, requestData, responseData, ...otherContext } = context;
        
        this.logError(error, {
            component: 'api',
            action: `${method || 'GET'} ${endpoint || 'unknown'}`,
            extra: {
                endpoint,
                method,
                requestData,
                responseData,
                ...otherContext.extra
            },
            tags: {
                errorType: 'api_error',
                ...otherContext.tags
            }
        });
    }

    /**
     * Log payment-related errors
     * @param {Error} error - The payment error
     * @param {Object} context - Additional context
     * @param {string} context.paymentType - Type of payment
     * @param {string} context.orderId - Order ID
     * @param {string} context.stage - Payment stage
     */
    static logPaymentError(error, context = {}) {
        const { paymentType, orderId, stage, ...otherContext } = context;
        
        this.logError(error, {
            component: 'payment',
            action: stage || 'unknown',
            extra: {
                paymentType,
                orderId,
                stage,
                ...otherContext.extra
            },
            tags: {
                errorType: 'payment_error',
                paymentType: paymentType || 'unknown',
                ...otherContext.tags
            }
        });
    }

    /**
     * Log printer-related errors
     * @param {Error} error - The printer error
     * @param {Object} context - Additional context
     * @param {string} context.action - Printer action
     * @param {string} context.receiptType - Type of receipt
     */
    static logPrinterError(error, context = {}) {
        const { action, receiptType, ...otherContext } = context;
        
        this.logError(error, {
            component: 'printer',
            action: action || 'unknown',
            extra: {
                receiptType,
                ...otherContext.extra
            },
            tags: {
                errorType: 'printer_error',
                ...otherContext.tags
            }
        });
    }
}

export default ErrorLogger; 